import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  ChangeEvent,
  FormEvent,
} from 'react';
import DataContext from '../../Context';
import { IPolicy } from '../../Models/PolicyInfo';
import { useNavigate } from 'react-router-dom';
import './CompanyInfo.css';
import Button from '../../Components/Button';
import ButtonGroup from '../../Components/ButtonGroup';
import Label from '../../Components/Label';
import TextInput from '../../Components/TextInput';
import FormRow from '../../Components/FormRow';
import ValidationError from '../../Components/ValidationError';
import Title from '../../Components/Title';
import InputMask from 'react-input-mask';
import useWarnRefresh from '../../hooks/useWarnRefresh';
import { ILocalizationContext } from '../../Localization/Models/ILocalizationContext';
import LocalizationContext from '../../Localization/LanguageContext';

const CompanyInfo = () => {
  const {
    companyName,
    setCompanyName,
    yourTitle,
    setYourTitle,
    phoneNumber,
    setPhoneNumber,
    yourExtension,
    setYourExtension,
    setProgressState,
    isSanitized,
  } = useContext(DataContext) as IPolicy;
  const { getValue } = useContext(LocalizationContext) as ILocalizationContext;

  const [cnRequired, setCnReq] = useState<boolean>(false);
  const [ytRequired, setYtReq] = useState<boolean>(false);
  const [pnRequired, setPnReq] = useState<boolean>(false);
  const [dataFail, setDataFail] = useState<boolean>(false);
  const scrollRef = useRef<HTMLInputElement>(null);

  useWarnRefresh();

  useEffect(() => {
    setProgressState(2);
  }, []);

  useEffect(() => {
    if (dataFail) scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [dataFail]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCompanyName(companyName.trim());
    setYourTitle(yourTitle.trim());
    setPnReq(true);

    if (IsDataValid()) {
      try {
        navigator('/createlogin', { replace: true });
      } catch (err) {
        setDataFail(true);
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
    if (!companyName || !yourTitle || !IsPhoneValid()) {
      CNblur();
      YTblur();
      PNblur();
    }
  };
  const GoNext = () => {
    if (!IsDataValid()) {
      setTimeout(handleErrorCheck, 10);
    }
  };
  const handleErrorCheck = () => {
    const input = document.querySelector(`.input-error,.check-error`);

    input?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'start',
    });
  };

  const CNblur = () => {
    if (!companyName.trim()) setCnReq(true);
  };

  const CNfocus = () => {
    setCnReq(false);
  };

  const YTblur = () => {
    if (!yourTitle.trim()) setYtReq(true);
  };

  const YTfocus = () => {
    setYtReq(false);
  };

  const PNblur = () => {
    if (!IsPhoneValid()) setPnReq(true);
  };

  const PNfocus = () => {
    setPnReq(false);
    let input = document.getElementById('MaskedPhone') as HTMLInputElement;
    input.setSelectionRange(1, 2);
  };

  let navigator = useNavigate();
  const GoBack = () => {
    navigator('/policyinfo', { replace: true });
  };

  const IsPhoneValid = (): boolean => {
    let unmasked = phoneNumber
      .replace('_', '')
      .replace('(', '')
      .replace(')', '')
      .replace('-', '')
      .replace(' ', '');
    if (unmasked.length !== 10) return false;
    return true;
  };

  let IsDataValid = () => {
    if (
      !companyName.trim() ||
      !yourTitle.trim() ||
      !isSanitized(companyName) ||
      !isSanitized(yourTitle)
    )
      return false;
    else if (IsPhoneValid() === false) return false;
    return true;
  };

  const handleExt = (e: ChangeEvent<HTMLInputElement>) => {
    setYourExtension(e.target.value);
  };

  const handlePhone = (e: ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
  };

  return (
    <div className='CompanyInfoPage'>
      <div className='container form-container px-4'>
        <form onSubmit={handleSubmit} className='company-info-form'>
          <FormRow>
            <Title
              contentDescription={getValue('CompanyInfo', 'Title')}
              instructionDescription={getValue('CompanyInfo', 'TitleSub')}
            />
          </FormRow>
          <FormRow>
            <Label>{getValue('CompanyInfo', 'BusinessLabel')}</Label>
            <TextInput
              inputId='CompanyName'
              inputValue={companyName}
              isInvalid={cnRequired || !isSanitized(companyName)}
              onChange={(e) => setCompanyName(e.target.value)}
              maxLength={64}
              onBlur={CNblur}
              onFocus={CNfocus}
              errorMessage={getValue('CompanyInfo', 'ValBusiness')}
            />
          </FormRow>
          <FormRow>
            <Label>{getValue('CompanyInfo', 'YourTitle')}</Label>
            <TextInput
              inputId='YourTitle'
              inputValue={yourTitle}
              isInvalid={ytRequired || !isSanitized(yourTitle)}
              onChange={(e) => setYourTitle(e.target.value)}
              onBlur={YTblur}
              onFocus={YTfocus}
              errorMessage={getValue('CompanyInfo', 'ValYourTitle')}
            />
          </FormRow>
          <FormRow>
            <div className='spread-header'>
              <Label>{getValue('CompanyInfo', 'PhoneLabel')}</Label>
              <Label>{getValue('CompanyInfo', 'ExtLabel')}</Label>
            </div>
            <InputMask
              id='MaskedPhone'
              className={
                !IsPhoneValid() && pnRequired
                  ? 'text-input p-2 input-error'
                  : 'text-input p-2'
              }
              value={phoneNumber}
              alwaysShowMask
              mask='(999) 999-9999'
              maskPlaceholder='(___) ___-____'
              inputMode='numeric'
              onChange={(e) => handlePhone(e)}
              onFocus={PNfocus}
              onBlur={PNblur}
            />
            <InputMask
              id='MaskedExtension'
              className='text-input w-25 p-2'
              value={yourExtension}
              alwaysShowMask
              mask='x9999'
              maskPlaceholder='x____'
              inputMode='numeric'
              onChange={(e) => handleExt(e)}
            />
            <ValidationError
              isInvalid={!IsPhoneValid() && pnRequired}
              message={getValue('CompanyInfo', 'ValPhone')}
            />
          </FormRow>
          <ButtonGroup>
            <span className='col-md-6 my-2'>
              <Button
                type='button'
                version='secondary'
                bootstrap='col-12'
                onClick={GoBack}
              >
                {getValue('CompanyInfo', 'Back')}
              </Button>
            </span>
            <span className='col-md-6 my-2'>
              <Button
                type='submit'
                bootstrap='col-12'
                version={IsDataValid() ? 'primary' : 'disabled'}
                onClick={GoNext}
              >
                {getValue('CompanyInfo', 'Next')}
              </Button>
            </span>
          </ButtonGroup>
        </form>
      </div>
      <div id='scrollbase' ref={scrollRef} />
    </div>
  );
};

export default CompanyInfo;
